import React, { useState, useEffect } from 'react';
import { HStack, Input, Select, Text, VStack } from 'native-base';
import { textStyle } from 'src/styles/textStyle';
import { getText } from 'src/utils/i18n';

interface IBaseDatePickerProps {
  value?: Date;
  onChange: (date: Date | null) => void;
  minDate?: Date;
  maxDate?: Date;
  label?: string;
}

export default function BaseDatePicker(props: IBaseDatePickerProps) {
  const { value, onChange, minDate, maxDate, label } = props;
  const MONTHS = [
    { value: 0, label: getText('january') },
    { value: 1, label: getText('february') },
    { value: 2, label: getText('march') },
    { value: 3, label: getText('april') },
    { value: 4, label: getText('mayMonth') },
    { value: 5, label: getText('june') },
    { value: 6, label: getText('july') },
    { value: 7, label: getText('august') },
    { value: 8, label: getText('september') },
    { value: 9, label: getText('october') },
    { value: 10, label: getText('november') },
    { value: 11, label: getText('december') },
  ];

  const [day, setDay] = useState<string>('');
  const [month, setMonth] = useState<string>('');
  const [year, setYear] = useState<string>('');

  // Initialize from value prop
  useEffect(() => {
    if (value && value instanceof Date && !isNaN(value.getTime())) {
      setDay(String(value.getDate()));
      setMonth(String(value.getMonth()));
      setYear(String(value.getFullYear()));
    } else {
      setDay('');
      setMonth('');
      setYear('');
    }
  }, [value]);

  // Helper to get max days in a month
  const getMaxDaysInMonth = (monthValue: number, yearValue: number): number => {
    if (monthValue === 1) { // February
      return (yearValue % 4 === 0 && yearValue % 100 !== 0) || yearValue % 400 === 0 ? 29 : 28;
    }
    return [3, 5, 8, 10].includes(monthValue) ? 30 : 31;
  };

  // Validate and construct date
  const constructDate = (dayVal: string, monthVal: string, yearVal: string): Date | null => {
    const dayNum = parseInt(dayVal, 10);
    const monthNum = parseInt(monthVal, 10);
    const yearNum = parseInt(yearVal, 10);

    if (isNaN(dayNum) || isNaN(monthNum) || isNaN(yearNum)) {
      return null;
    }

    if (yearNum < 1900 || yearNum > 2099) {
      return null;
    }

    const maxDays = getMaxDaysInMonth(monthNum, yearNum);
    if (dayNum < 1 || dayNum > maxDays) {
      return null;
    }

    const date = new Date(yearNum, monthNum, dayNum);

    // Check min/max constraints
    if (minDate && date < minDate) {
      return null;
    }
    if (maxDate && date > maxDate) {
      return null;
    }

    return date;
  };

  const handleDayChange = (value: string) => {
    // Allow only numbers
    const cleanValue = value.replace(/[^0-9]/g, '');
    if (cleanValue.length <= 2) {
      setDay(cleanValue);
      const date = constructDate(cleanValue, month, year);
      onChange(date);
    }
  };

  const handleMonthChange = (value: string) => {
    setMonth(value);
    const date = constructDate(day, value, year);
    onChange(date);
  };

  const handleYearChange = (value: string) => {
    // Allow only numbers
    const cleanValue = value.replace(/[^0-9]/g, '');
    if (cleanValue.length <= 4) {
      setYear(cleanValue);
      const date = constructDate(day, month, cleanValue);
      onChange(date);
    }
  };

  return (
    <VStack space={2}>
      {label && <Text style={textStyle.subSectionText}>{label}</Text>}
      <HStack space={2} alignItems="center">
        <Input
          flex={1}
          placeholder={getText('day') || 'Day'}
          value={day}
          onChangeText={handleDayChange}
          keyboardType="numeric"
          inputMode="numeric"
          maxLength={2}
          type="text"
        />
        <Select
          flex={2}
          placeholder={getText('month') || 'Month'}
          selectedValue={month}
          onValueChange={handleMonthChange}>
          {MONTHS.map((m) => (
            <Select.Item key={m.value} label={m.label} value={String(m.value)} />
          ))}
        </Select>
        <Input
          flex={1.5}
          placeholder={getText('year') || 'Year'}
          value={year}
          onChangeText={handleYearChange}
          keyboardType="numeric"
          inputMode="numeric"
          maxLength={4}
          type="text"
        />
      </HStack>
    </VStack>
  );
}
